import { FC, useEffect, useState, useContext, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { LoaderContext } from "src/context/loaderContext";
import { ToastContext } from "src/context/toastContext";
import { ToastTypes } from "src/types";
import {DOCTOR_HEADER_LISTING} from "../../constants/cases";
import { Doctor, DoctorsAPIResponse} from "./types";
import {AuthContext} from "../../context/authContext";
import SignatureModal from "../../components/modals/signatureModals/addSignatureModal";
import AddSignatureButton from "../../components/addSignatureButton";

const DoctorListingPage: FC = (): JSX.Element => {
    const { setLoader, hideLoader } = useContext(LoaderContext);
    const { showToast } = useContext(ToastContext);
    const { userData } = useContext(AuthContext);
    const [doctorList, setDoctorList] = useState<Doctor[]>([]);
    const [isSignatureModalVisible, setIsSignatureModalVisible] = useState(false);
    const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(null);
    const [refetchTrigger, setRefetchTrigger] = useState(false);
    const [userSignature, setUserSignature] = useState<string | null>(null);
    const [pendingSignatureFile, setPendingSignatureFile] = useState<File | null>(null);

    // API Hook for uploading signature (manually triggered only)
    const {
        data: signatureUploadResponse,
        error: signatureUploadError,
        mutate: uploadSignatureMutate,
        removeErrorAndData: clearSignatureState,
    } = useFetch<any>(
        APIEndpoints.UPDATE_SIGNATURE,
        {},                      // options
        { id: selectedDoctorId },// params
        false                    // Do not auto-fetch
    );

    const {
        data: doctorSignaturesData,
        error: doctorSignaturesError,
        mutate: doctorSignaturesMutate,
    } = useFetch<DoctorsAPIResponse>(
        APIEndpoints.DOCTORS_SIGNATURE_LISTING,
        {},
        {},
        false
    );

    // Trigger signature upload without async/await
    const handleSaveSignature = (file: File) => {
        if (!selectedDoctorId) return;

        clearSignatureState();
        setLoader();

        const formData = new FormData();
        formData.append("global_doctor[signature_data]", file);

        // Preview URL is only for UI (optional)
        setPendingSignatureFile(file);

        // Trigger upload
        uploadSignatureMutate(formData);
    };

    useLayoutEffect(() => setLoader(), [])

    // Handle upload result
    useEffect(() => {
        if (signatureUploadResponse || signatureUploadError) {
            hideLoader();
            if(signatureUploadResponse){
                if (signatureUploadResponse.status.code === 200) {
                    showToast(['Signature uploaded successfully'], ToastTypes.SUCCESS);
                    setRefetchTrigger(true); // trigger reload
                } else {
                    showToast(['Failed to upload signature'], ToastTypes.ERROR);
                }
            }
            setIsSignatureModalVisible(false);
            setSelectedDoctorId(null);
            setUserSignature(null); // clear preview
        }
    }, [signatureUploadResponse, signatureUploadError]);

    useEffect(() => {
        setLoader();
        doctorSignaturesMutate();
    }, []);

    useEffect(() => {
        if (refetchTrigger) {
            setLoader();
            doctorSignaturesMutate();
            setRefetchTrigger(false)
        }
    }, [refetchTrigger]);


    useEffect(() => {
        if (doctorSignaturesData) {
            setDoctorList(doctorSignaturesData.result);
            hideLoader();
        }

        if (doctorSignaturesError) {
            hideLoader();
            showToast(["Unable to fetch doctor signatures right now."], ToastTypes.ERROR);
        }
    }, [doctorSignaturesData, doctorSignaturesError]);

    const handleAddSignatureClick = (doctorId: number) => {
        setSelectedDoctorId(doctorId);
        setIsSignatureModalVisible(true);
    };

    return (
        <div className="container-fluid cases-page overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill">
            <div className="card my-4">
                <div className="card-header">
                    <h1 className="mb-0">Signature Management</h1>
                </div>
                <div className="card-body">
                    <div className="table-responsive-sm table-responsive-md table-responsive-lg">
                        <table className="table table-striped table-sm m-0">
                            <thead>
                            <tr className="text-center">
                                {DOCTOR_HEADER_LISTING.map((header, idx) => (
                                    <th className="text-white" key={idx}>
                                        {header.label}
                                    </th>
                                ))}
                                <th className="text-white">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {doctorList.length > 0 ? (
                                doctorList.map((doc) => (
                                    <tr key={doc.id} className="text-center align-middle">
                                        <td>{doc.id}</td>
                                        <td>{`${doc.fullname}`}</td>
                                        <td>{`${doc.npi}`}</td>
                                        <td>
                                            {doc.signature_data ?
                                                <img className="img-fluid" alt="Signature" style={{maxHeight: "60px"}}
                                                    src={`data:image/png;base64,${doc.signature_data}`}
                                                />
                                                :<></>
                                            }
                                        </td>
                                        <td>
                                            <button
                                                className={`btn btn-sm ${doc.signature_data ? "btn-outline-primary" : "btn-primary"}`}
                                                onClick={() => handleAddSignatureClick(doc.id)}
                                            >
                                                {doc.signature_data ? "Edit" : "Add"}
                                            </button>
                                        </td>
                                        {/*{doc.signature_data ? (*/}
                                        {/*        <>*/}
                                        {/*            <img*/}
                                        {/*                src={`data:image/png;base64,${doc.signature_data}`}*/}
                                        {/*                alt="Signature"*/}
                                        {/*                style={{maxHeight: "60px"}}*/}
                                        {/*            />*/}
                                        {/*            <AddSignatureButton*/}
                                        {/*                userSignature={doc.signature_data}*/}
                                        {/*                onClick={() => handleAddSignatureClick(doc.id)}*/}
                                        {/*                onlyButton={true}*/}
                                        {/*                isEditSign={true}*/}
                                        {/*            />*/}
                                        {/*        </>*/}
                                        {/*    ) : (*/}
                                        {/*        <AddSignatureButton*/}
                                        {/*            userSignature={null}*/}
                                        {/*            onClick={() => handleAddSignatureClick(doc.id)}*/}
                                        {/*            onlyButton={true}*/}
                                        {/*        />*/}
                                        {/*)}*/}
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan={12} className="text-center">No doctors found.</td></tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                    <SignatureModal
                        isOpen={isSignatureModalVisible}
                        onClose={() => {
                            setIsSignatureModalVisible(false);
                            setSelectedDoctorId(null);
                            setUserSignature(null); // clear preview
                        }}
                        onSave={handleSaveSignature}
                    />
                </div>
            </div>
        </div>
    );
};

export default DoctorListingPage;
